import React, { useMemo, useState } from 'react';
import { cn } from '@/lib/utils';
import { Tabs, TabsList, TabsTrigger } from '../components/Tabs';
import { featureCards } from '../constants/mockData';
import { ASSETS } from '../constants/assets';
import { MapPin, Search, Filter, Compass, Home as HomeIcon, Heart, ChevronLeft } from 'lucide-react';
import { Badge } from '../components/Badge';
const categories = ['全部', '旅游休闲', '文化场馆', '美食购物', '人文宿州', '酒店住宿', '智慧热图', '赛事文旅'];
const districts = ['全部', '埇桥区', '砀山县', '萧县', '灵璧县', '泗县'];
export function Explore() {
  const [activeTab, setActiveTab] = useState('全部');
  const [activeDistrict, setActiveDistrict] = useState('全部');
  const filteredCards = useMemo(() => {
    return featureCards.filter(card => {
      const matchTab = activeTab === '全部' || card.category.includes(activeTab) || (card as any).section?.includes(activeTab.toLowerCase()) || activeTab === '人文宿州' && card.category === '人文宿州';
      const matchDistrict = activeDistrict === '全部' || (card as any).county === activeDistrict;
      return matchTab && matchDistrict;
    });
  }, [activeTab, activeDistrict]);
  return <div className="min-h-screen bg-[#EEF8F8] pb-24" data-id="element-144">
      {/* Search Bar (Solid) */}
      <header className="fixed top-0 left-0 right-0 z-50 h-20 bg-white flex items-center px-4 shadow-sm" data-id="element-145">
        <div className="flex-1 h-10 bg-gray-100 rounded-full flex items-center px-4 gap-2" data-id="element-146">
          <Search className="size-4 text-gray-400" data-id="element-147" />
          <span className="text-sm text-gray-500" data-id="element-148">搜索景区/场馆/美食/酒店</span>
        </div>
        <div className="ml-3 bg-gray-100 p-2.5 rounded-full" data-id="element-149">
          <Filter className="size-4 text-gray-500" data-id="element-150" />
        </div>
      </header>

      <div className="pt-20" data-id="element-151">
        {/* City Recommendation Banner */}
        <div className="relative h-48 w-full overflow-hidden" data-id="element-152">
          <img src={ASSETS.heroXinbianheNight} alt="Suzhou Night" className="w-full h-full object-cover" data-id="element-153" />
          <div className="absolute inset-0 bg-black/40 flex flex-col justify-center px-6" data-id="element-154">
            <div className="flex gap-2 mb-2" data-id="element-155">
              {['文旅资源', '城市休闲', '研学旅行', '非遗体验'].map(tag => <Badge key={tag} className="bg-white/20 text-white border-none text-[16rpx]" data-id="element-156">
                  {tag}
                </Badge>)}
            </div>
            <h2 className="text-2xl font-bold text-white" data-id="element-157">自在宿城</h2>
            <p className="text-white/80 text-xs mt-1" data-id="element-158">
              发现宿州的每一种打开方式
            </p>
          </div>
        </div>

        <div className="px-4 mt-6 flex flex-col gap-6" data-id="element-159">
          {/* Category Navigation */}
          <div className="overflow-x-auto no-scrollbar -mx-4 px-4" data-id="element-160">
            <div className="flex gap-6 pb-2" data-id="element-161">
              {categories.map(cat => <div key={cat} onClick={() => setActiveTab(cat)} className={`text-base transition-all whitespace-nowrap relative cursor-pointer ${activeTab === cat ? 'text-[#66CADA] font-bold after:content-[""] after:absolute after:-bottom-2 after:left-1/2 after:-translate-x-1/2 after:w-6 after:h-1 after:bg-[#66CADA] after:rounded-full' : 'text-[#666666]'}`} data-id="element-162">
                  {cat}
                </div>)}
            </div>
          </div>

          {/* District Filter */}
          <div className="flex items-center gap-2 overflow-x-auto no-scrollbar -mx-4 px-4" data-id="element-163">
            <div className="bg-white p-2 rounded-lg shrink-0 shadow-sm border border-gray-100" data-id="element-164">
              <Filter className="size-4 text-[#66CADA]" data-id="element-165" />
            </div>
            {districts.map(dist => <Badge key={dist} onClick={() => setActiveDistrict(dist)} className={`cursor-pointer px-4 py-1.5 rounded-full border-none transition-all whitespace-nowrap ${activeDistrict === dist ? 'bg-[#66CADA] text-white shadow-md shadow-[#66CADA]/20' : 'bg-white text-[#666666] border border-gray-100 hover:bg-gray-50'}`} data-id="element-166">
                {dist}
              </Badge>)}
          </div>

          {/* Today's Recommendations (Horizontal) - Only show when no specific filter */}
          {activeTab === '全部' && activeDistrict === '全部' && <div className="flex flex-col gap-4" data-id="element-167">
              <div className="flex items-center justify-between" data-id="element-168">
                <div data-id="element-169">
                  <h2 className="text-xl font-bold text-[#333333]" data-id="element-170">今日推荐</h2>
                  <p className="text-[16rpx] text-[#999999] mt-0.5" data-id="element-171">
                    根据热度与场景为你精选
                  </p>
                </div>
                <span className="text-xs text-[#999999]" data-id="element-172">查看更多</span>
              </div>
              <div className="flex overflow-x-auto no-scrollbar gap-4 -mx-4 px-4 pb-2" data-id="element-173">
                {featureCards.slice(0, 3).map(card => <div key={card.id} onClick={() => window.location.href = `/scenic-detail/${card.id}`} className="shrink-0 w-64 bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 cursor-pointer active:opacity-80" data-id="element-174">
                    <div className="h-36 relative" data-id="element-175">
                      <img src={card.image} alt={card.title} className="w-full h-full object-cover" data-id="element-176" />
                      <div className={`absolute top-2 right-2 bg-black/40 backdrop-blur-sm text-white px-2 py-0.5 rounded-full ${card.category === '旅游休闲' ? 'text-[18rpx]' : 'text-[16rpx]'}`} data-id="element-177">
                        {card.category}
                      </div>
                      <div className="absolute bottom-2 left-2 flex gap-1" data-id="element-178">
                        <span className="bg-orange-500/90 backdrop-blur-sm text-white text-[16rpx] px-1.5 py-0.5 rounded" data-id="element-179">
                          {(card as any).comfort || '舒适'}
                        </span>
                        {card.tags?.slice(0, 1).map(tag => <span key={tag} className="bg-[#66CADA]/80 backdrop-blur-sm text-white text-[16rpx] px-1.5 py-0.5 rounded" data-id="element-180">
                            {tag}
                          </span>)}
                      </div>
                    </div>
                    <div className="p-3" data-id="element-181">
                      <div className="flex items-center justify-between mb-1" data-id="element-182">
                        <h3 className="font-bold text-[#333333] truncate flex-1" data-id="element-183">
                          {card.title}
                        </h3>
                        <span className="text-[#FF4D4F] text-[16rpx] font-bold" data-id="element-184">
                          {card.heatScore}℃
                        </span>
                      </div>
                      <div className="flex items-center justify-between" data-id="element-185">
                        <div className="flex items-center gap-1 text-[#999999]" data-id="element-186">
                          <MapPin className="size-3" data-id="element-187" />
                          <span className="text-[14px]" data-id="element-188">{card.distance}</span>
                        </div>
                        <span className="text-[16rpx] text-[#999999]" data-id="element-189">
                          {card.county}
                        </span>
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>}

          {/* All Resources List */}
          <div className="flex flex-col gap-4" data-id="element-190">
            <h2 className="text-xl font-bold text-[#333333]" data-id="element-191">
              {activeTab === '全部' && activeDistrict === '全部' ? '全部资源' : `搜索结果 (${filteredCards.length})`}
            </h2>
            <div className="grid grid-cols-1 gap-4" data-id="element-192">
              {filteredCards.length > 0 ? filteredCards.map(card => <div key={card.id} onClick={() => window.location.href = `/scenic-detail/${card.id}`} className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 flex h-32 cursor-pointer active:opacity-80" data-id="element-193">
                    <div className="w-32 shrink-0" data-id="element-194">
                      <img src={card.image} alt={card.title} className="w-full h-full object-cover" data-id="element-195" />
                    </div>
                    <div className="p-3 flex flex-col justify-between flex-1 min-w-0" data-id="element-196">
                      <div data-id="element-197">
                        <div className="flex items-center justify-between" data-id="element-198">
                          <h3 className="text-base font-bold text-[#333333] truncate flex-1" data-id="element-199">
                            {card.title}
                          </h3>
                          <span className="text-[16rpx] text-[#999999] ml-2" data-id="element-200">
                            {(card as any).county}
                          </span>
                        </div>
                        <p className="text-xs text-[#999999] line-clamp-1 mt-1" data-id="element-201">
                          {card.description}
                        </p>
                      </div>
                      <div className="flex items-center justify-between mt-2" data-id="element-202">
                        <div className="flex items-center gap-1.5" data-id="element-203">
                          <div className="flex items-center gap-0.5 text-[#999999]" data-id="element-204">
                            <MapPin className="size-3.5" data-id="element-205" />
                            <span className="text-[12px] font-[300]" data-id="element-206">
                              {card.distance}
                            </span>
                          </div>
                          <span className={cn(`text-[16rpx] px-1 py-0.5 rounded whitespace-nowrap ${card.heatScore >= 90 ? 'bg-red-50 text-red-500' : 'bg-orange-50 text-orange-500'}`, 'text-[12px]')} data-id="element-207">
                            {card.heatScore >= 90 ? '爆热' : '热门'}{' '}
                            {card.heatScore}
                          </span>
                        </div>
                        <Badge variant="secondary" className={`bg-[#EEF8F8] text-[#66CADA] border-none px-2 py-0.5 shrink-0 ${card.category === '旅游休闲' ? 'text-[18rpx]' : 'text-[16rpx]'}`} data-id="element-208">
                          {card.category}
                        </Badge>
                      </div>
                    </div>
                  </div>) : <div className="py-20 flex flex-col items-center justify-center text-gray-400 gap-2" data-id="element-209">
                  <img src={ASSETS.emptyState} alt="Empty" className="size-24 opacity-50" data-id="element-210" />
                  <span className="text-sm" data-id="element-211">暂无匹配资源</span>
                </div>}
            </div>
          </div>
        </div>
      </div>

      {/* Tab Bar */}
      <nav className="fixed bottom-0 left-0 right-0 h-20 bg-white/95 backdrop-blur-md border-t border-gray-100 flex items-center justify-around px-6 z-50 shadow-[0_-4px_12px_rgba(0,0,0,0.03)]" data-id="element-212">
        <div onClick={() => window.location.href = '/'} className="flex flex-col items-center gap-1 text-[#999999] cursor-pointer hover:text-[#66CADA] transition-colors" data-id="element-213">
          <HomeIcon className="size-6" data-id="element-214" />
          <span className="text-[22rpx]" data-id="element-215">首页</span>
        </div>
        <div className="flex flex-col items-center gap-1 text-[#66CADA] cursor-pointer" data-id="element-216">
          <Compass className="size-6" data-id="element-217" />
          <span className="text-[22rpx] font-bold" data-id="element-218">游宿州</span>
        </div>
        <div onClick={() => window.location.href = '/service'} className="flex flex-col items-center gap-1 text-[#999999] cursor-pointer hover:text-[#66CADA] transition-colors" data-id="element-219">
          <Heart className="size-6" data-id="element-220" />
          <span className="text-[22rpx]" data-id="element-221">享服务</span>
        </div>
      </nav>
    </div>;
}